// src/pages/BatchProcessing.jsx
// Phase E4 — Batch Processing. Group clients for a financial year, track
// each one's AFS completion status, and send director sign-off requests
// to the whole batch in one action.
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Package } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { getClients, getBatches, getBatch, createBatch, sendBatchSignoffs } from '../api/kailux'
import EmptyState from '../components/EmptyState'
import Spinner from '../components/Spinner'

import Icon from '../components/Icon'

import { T } from '../theme/theme'
const AFS_STATUS = {
  not_started: { label:'Not started', color:T.mt, bg:T.dm },
  upload:      { label:'In progress', color:T.bl, bg:T.bld },
  mapping:     { label:'In progress', color:T.bl, bg:T.bld },
  review:      { label:'In review',   color:T.or, bg:T.ord },
  complete:    { label:'Complete',    color:T.gn, bg:T.gnd },
}

const SIGNOFF_STATUS = {
  not_sent: { label:'Not sent',  color:T.mt },
  pending:  { label:'Awaiting signature', color:T.or },
  signed:   { label:<>Signed <Icon name="check" /></>,  color:T.gn },
  expired:  { label:'Expired',   color:T.mt },
}

const currentYear = new Date().getFullYear()

export default function BatchProcessing() {
  const { firm } = useAuth()
  const [clients, setClients] = useState([])
  const [batches, setBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeBatch, setActiveBatch] = useState(null)
  const [loadingDetail, setLoadingDetail] = useState(false)

  const [showCreate, setShowCreate] = useState(false)
  const [label, setLabel]           = useState('')
  const [financialYear, setFinancialYear] = useState(String(currentYear))
  const [selectedClients, setSelectedClients] = useState({})
  const [creating, setCreating]     = useState(false)

  // sign-off panel state
  const [signoffDrafts, setSignoffDrafts] = useState({}) // {client_id: {name, email}}
  const [sending, setSending] = useState(false)
  const [sendResults, setSendResults] = useState(null)

  const loadList = () => {
    if (!firm?.id) return
    Promise.all([getClients(firm.id), getBatches(firm.id)])
      .then(([c, b]) => { setClients(c.data || []); setBatches(b.data || []) })
      .catch(console.error)
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadList() }, [firm?.id])

  const openBatch = (id) => {
    setLoadingDetail(true)
    setSendResults(null)
    getBatch(id).then(res => {
      const data = res.data || {}
      setActiveBatch({ ...data, clients: data.clients || [], summary: data.summary || { total: 0, complete: 0, pending: 0 } })
      const drafts = {}
      ;(data.clients || []).forEach(c => {
        drafts[c.client_id] = { name: c.signing_director || '', email: '' }
      })
      setSignoffDrafts(drafts)
    }).catch(console.error)
    .finally(() => setLoadingDetail(false))
  }

  const toggleClient = (id) => setSelectedClients(s => ({ ...s, [id]: !s[id] }))

  const handleCreate = async () => {
    const clientIds = Object.keys(selectedClients).filter(id => selectedClients[id])
    if (clientIds.length === 0) return alert('Select at least one client')
    setCreating(true)
    try {
      const res = await createBatch({
        firm_id: firm.id, label: label.trim(), client_ids: clientIds, financial_year: financialYear,
      })
      setLabel(''); setSelectedClients({}); setShowCreate(false)
      loadList()
      openBatch(res.data.id)
    } catch {
      alert('Could not create batch.')
    } finally {
      setCreating(false)
    }
  }

  const updateDraft = (clientId, field, value) =>
    setSignoffDrafts(d => ({ ...d, [clientId]: { ...d[clientId], [field]: value } }))

  const sendSignoffs = async () => {
    if (!activeBatch) return
    const eligible = activeBatch.clients.filter(c => c.afs_status === 'complete' && c.signoff_status !== 'signed')
    const signoffs = eligible
      .map(c => ({ client_id: c.client_id, director_name: signoffDrafts[c.client_id]?.name || '',
                    director_email: signoffDrafts[c.client_id]?.email || '' }))
      .filter(s => s.director_email)

    if (signoffs.length === 0) return alert('Enter at least one director email before sending.')

    setSending(true)
    try {
      const res = await sendBatchSignoffs(activeBatch.id, { firm_name: firm?.name || '', signoffs })
      setSendResults(res.data.results)
      openBatch(activeBatch.id)
    } catch {
      alert('Could not send sign-off requests.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif",
      color:T.tx, padding:'32px' }}>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px' }}>
        <div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:'800' }}>
            Batch Processing
          </div>
          <div style={{ fontSize:'13px', fontWeight:'400', color:T.mt, marginTop:'4px' }}>
            Group year-end clients together, track AFS completion at a glance, and
            send all director sign-off requests in one go
          </div>
        </div>
        <button onClick={() => setShowCreate(s => !s)}
          style={{ padding:'10px 22px', borderRadius:'8px', border:'none',
            background:T.prg, color:T.onpri,
            fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
          {showCreate ? 'Cancel' : '+ New Batch'}
        </button>
      </div>

      {showCreate && (
        <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'24px', marginBottom:'24px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'18px' }}>
            <div>
              <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'6px' }}>Batch label</label>
              <input value={label} onChange={e => setLabel(e.target.value)}
                placeholder="e.g. February 2026 year-ends"
                style={{ width:'100%', padding:'10px 12px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                  background:T.s2, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'14px' }} />
            </div>
            <div>
              <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'6px' }}>Financial year</label>
              <input value={financialYear} onChange={e => setFinancialYear(e.target.value)}
                placeholder={String(currentYear)}
                style={{ width:'100%', padding:'10px 12px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                  background:T.s2, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'14px' }} />
            </div>
          </div>

          <div style={{ fontSize:'13px', fontWeight:'700', color:T.lt, marginBottom:'10px' }}>
            Select clients ({Object.values(selectedClients).filter(Boolean).length} selected)
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px', marginBottom:'18px',
            maxHeight:'260px', overflowY:'auto' }}>
            {clients.map(c => (
              <label key={c.id} onClick={() => toggleClient(c.id)}
                style={{ display:'flex', alignItems:'center', gap:'8px', padding:'9px 12px',
                  borderRadius:'8px', cursor:'pointer',
                  border:`1.5px solid ${selectedClients[c.id] ? T.or : T.bd}`,
                  background: selectedClients[c.id] ? T.ord : T.s2 }}>
                <div style={{ width:'15px', height:'15px', borderRadius:'6px', flexShrink:0,
                  border:`1.5px solid ${selectedClients[c.id] ? T.or : T.mt}`,
                  background: selectedClients[c.id] ? T.or : 'transparent',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'10px', color:T.onpri }}>
                  {selectedClients[c.id] && <Icon name="check" />}
                </div>
                <span style={{ fontSize:'13px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {c.name}
                </span>
              </label>
            ))}
          </div>

          <button onClick={handleCreate} disabled={creating}
            style={{ padding:'12px 28px', borderRadius:'8px', border:'none',
              background:T.prg, color:T.onpri,
              fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700',
              cursor: creating ? 'default' : 'pointer' }}>
            {creating ? 'Creating…' : 'Create batch →'}
          </button>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns: activeBatch ? '320px 1fr' : '1fr', gap:'20px' }}>

        {/* ── Batch list ── */}
        <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', overflow:'hidden' }}>
          <div style={{ padding:'16px 20px', borderBottom:`1px solid ${T.bd}`, fontSize:'14px', fontWeight:'700' }}>
            Batches
          </div>
          {loading ? (
            <div style={{ padding:'32px', textAlign:'center' }}><Spinner fontSize='14px'/></div>
          ) : batches.length === 0 ? (
            <EmptyState icon={Package} title="No batches yet" subtitle="Create one to get started" />
          ) : batches.map(b => {
            const summary = b.summary || { total: 0, complete: 0, pending: 0 }
            return (
            <div key={b.id} onClick={() => openBatch(b.id)}
              style={{ padding:'14px 20px', borderBottom:`1px solid ${T.bd}`, cursor:'pointer',
                background: activeBatch?.id === b.id ? T.ord : 'transparent' }}>
              <div style={{ fontSize:'14px', fontWeight:'700' }}>{b.label}</div>
              <div style={{ fontSize:'12px', color:T.mt, marginTop:'4px' }}>
                FY{b.financial_year} · {summary.complete}/{summary.total} AFS complete
              </div>
              <div style={{ height:'5px', borderRadius:'6px', background:T.dm, marginTop:'8px' }}>
                <div style={{ height:'100%', borderRadius:'6px',
                  width: `${summary.total ? (summary.complete/summary.total)*100 : 0}%`,
                  background: T.gn, transition:'width 0.3s' }} />
              </div>
            </div>
          )})}
        </div>

        {/* ── Batch detail ── */}
        {activeBatch && (
          <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:`1px solid ${T.bd}`,
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:'14px', fontWeight:'700' }}>{activeBatch.label}</div>
                <div style={{ fontSize:'12px', color:T.mt, marginTop:'2px' }}>
                  FY{activeBatch.financial_year} · {(activeBatch.summary || {}).total || 0} clients
                </div>
              </div>
              <button onClick={sendSignoffs} disabled={sending}
                style={{ padding:'9px 20px', borderRadius:'8px', border:'none',
                  background:T.prg, color:T.onpri,
                  fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'700',
                  cursor: sending ? 'default' : 'pointer' }}>
                {sending ? 'Sending…' : <><Icon name="mail" /> Send sign-off requests</>}
              </button>
            </div>

            {loadingDetail ? (
              <div style={{ padding:'32px', textAlign:'center' }}><Spinner fontSize='14px'/></div>
            ) : (
              <>
                {sendResults && (
                  <div style={{ padding:'12px 20px', background:T.bld, borderBottom:`1px solid ${T.bd}`, fontSize:'13px' }}>
                    {sendResults.map(r => (
                      <div key={r.client_id} style={{ marginBottom:'2px' }}>
                        {r.status === 'sent' ? <Icon name="check" /> : '·'} {activeBatch.clients.find(c=>c.client_id===r.client_id)?.client_name}
                        {' — '}{r.status === 'sent' ? 'sign-off sent' : `skipped (${r.reason})`}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ padding:'8px 20px', display:'grid',
                  gridTemplateColumns:'1.5fr 1fr 1fr 1fr 1fr', gap:'8px',
                  fontSize:'10px', color:T.mt, textTransform:'uppercase', letterSpacing:'0.5px',
                  borderBottom:`1px solid ${T.bd}`, paddingBottom:'10px', paddingTop:'14px' }}>
                  <div>Client</div><div>AFS Status</div><div>Sign-off</div>
                  <div>Director name</div><div>Director email</div>
                </div>

                {activeBatch.clients.map((c, i) => {
                  const afsBadge = AFS_STATUS[c.afs_status] || AFS_STATUS.not_started
                  const soBadge  = SIGNOFF_STATUS[c.signoff_status] || SIGNOFF_STATUS.not_sent
                  const eligible = c.afs_status === 'complete' && c.signoff_status !== 'signed'
                  return (
                    <div key={c.client_id} style={{ padding:'12px 20px', display:'grid',
                      gridTemplateColumns:'1.5fr 1fr 1fr 1fr 1fr', gap:'8px', alignItems:'center',
                      borderBottom: i < activeBatch.clients.length-1 ? `1px solid ${T.bd}` : 'none' }}>
                      <div style={{ fontSize:'14px', fontWeight:'600' }}>{c.client_name}</div>
                      <div>
                        <span style={{ fontSize:'12px', fontWeight:'700', padding:'3px 9px', borderRadius:'6px',
                          color: afsBadge.color, background: afsBadge.bg }}>
                          {afsBadge.label}
                        </span>
                      </div>
                      <div>
                        <span style={{ fontSize:'12px', fontWeight:'700', color: soBadge.color }}>
                          {soBadge.label}
                        </span>
                      </div>
                      <input value={signoffDrafts[c.client_id]?.name || ''}
                        disabled={!eligible}
                        onChange={e => updateDraft(c.client_id, 'name', e.target.value)}
                        placeholder="Director name"
                        style={{ width:'100%', padding:'7px 10px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                          background: eligible ? T.s2 : T.dm, color:T.tx, fontFamily:"'Outfit',sans-serif",
                          fontSize:'13px', opacity: eligible ? 1 : 0.5 }} />
                      <input value={signoffDrafts[c.client_id]?.email || ''}
                        disabled={!eligible}
                        onChange={e => updateDraft(c.client_id, 'email', e.target.value)}
                        placeholder="director@client.co.za"
                        style={{ width:'100%', padding:'7px 10px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                          background: eligible ? T.s2 : T.dm, color:T.tx, fontFamily:"'Outfit',sans-serif",
                          fontSize:'13px', opacity: eligible ? 1 : 0.5 }} />
                    </div>
                  )
                })}

                <div style={{ padding:'14px 20px', fontSize:'12px', color:T.mt, borderTop:`1px solid ${T.bd}` }}>
                  Clients not yet showing "Complete" need their AFS generated first — head to{' '}
                  <Link to="/trial-balance" style={{ color:T.or, textDecoration:'none' }}>Trial Balance</Link> or{' '}
                  <Link to="/bank-statement" style={{ color:T.or, textDecoration:'none' }}>Bank Statement</Link> upload for each.
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
